import { useAuth } from "@/hooks/auth/useAuth";
import { gql, useQuery } from "@apollo/client";
import {
    ArrowLeft,
    Calendar,
    CalendarDays,
    ChartNoAxesColumn,
    GraduationCap,
    Home,
    Info,
    Menu,
    Radio,
    Users,
    X,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

const STREAM_TEAM_NAV_FEATURE_FLAGS_QUERY = gql`
    query StreamTeamNavFeatureFlags {
        featureFlags {
            streamTeamAcademyEnabled
        }
    }
`;

export default function StreamTeamNav() {
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { isAuthenticated, user } = useAuth();
    const { data } = useQuery(STREAM_TEAM_NAV_FEATURE_FLAGS_QUERY, {
        fetchPolicy: "cache-and-network",
    });

    const isAcademyEnabled = Boolean(
        data?.featureFlags?.streamTeamAcademyEnabled,
    );

    const navItems = [
        { path: "/", label: "Home", icon: Home },
        { path: "/about", label: "About", icon: Info },
        { path: "/team", label: "Members", icon: Users },
        ...(isAcademyEnabled
            ? [{ path: "/academy", label: "Academy", icon: GraduationCap }]
            : []),
        { path: "/live", label: "Live", icon: Radio },
        { path: "/schedule", label: "Schedule", icon: Calendar },
        { path: "/events", label: "Events", icon: CalendarDays },
        {
            path: "/leaderboards",
            label: "Leaderboards",
            icon: ChartNoAxesColumn,
        },
    ];

    return (
        <nav className="border-brand_stream_team_gold border-b-2 bg-stone-950 shadow-lg">
            <div className="mx-auto px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center gap-2">
                        {/* Mobile logo */}
                        {/* <img
                            src="/vrx-rune-logo.svg"
                            alt="Ravenguard Logo"
                            className="h-16 md:hidden"
                        /> */}
                        {/* Desktop logo */}
                        <img
                            src="/ravenguard-logo-ps.svg"
                            alt="Ravenguard Logo"
                            className="h-12 md:h-16"
                        />
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden gap-2 md:flex">
                        {isAuthenticated && user?.twitchUsername && (
                            <span className="font-garamond inline-flex items-center rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-300">
                                Signed in as {user.twitchUsername}
                            </span>
                        )}
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`font-garamond flex items-center gap-2 rounded-lg px-4 py-2 text-base font-medium transition-all ${
                                        isActive
                                            ? "bg-brand text-zinc-900"
                                            : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                                    }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                        <a
                            href="https://village.vanderheim.com"
                            className="font-garamond flex items-center gap-2 rounded-lg px-4 py-2 text-base font-medium text-zinc-300 transition-all hover:bg-zinc-800 hover:text-white"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span>Back to Village</span>
                        </a>
                    </div>

                    {/* Mobile Hamburger Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="rounded-lg p-2 text-zinc-300 hover:bg-zinc-800 hover:text-white md:hidden"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? (
                            <X className="h-6 w-6" />
                        ) : (
                            <Menu className="h-6 w-6" />
                        )}
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                {mobileMenuOpen && (
                    <div className="mt-4 space-y-2 md:hidden">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path;

                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`font-garamond flex items-center gap-3 rounded-lg px-4 py-3 text-base font-medium transition-all ${
                                        isActive
                                            ? "bg-brand text-zinc-900"
                                            : "text-zinc-300 hover:bg-zinc-800 hover:text-white"
                                    }`}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                        <a
                            href="https://vanderheim.com"
                            onClick={() => setMobileMenuOpen(false)}
                            className="font-garamond flex items-center gap-3 rounded-lg px-4 py-3 text-base font-medium text-zinc-300 transition-all hover:bg-zinc-800 hover:text-white"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span>Back to Village</span>
                        </a>
                    </div>
                )}
            </div>
        </nav>
    );
}
